/*
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import { FC } from 'react';
import { ListGroup } from 'react-bootstrap';
import { NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Counts, Tag } from '@/components';
import { pathFactory } from '@/router/pathFactory';

interface IProps {
  data: any[];
}

/** 置顶摘要：首句换段，保留开头导语，去掉后面的指南正文。 */
function pinExcerpt(raw: string) {
  const text = String(raw || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split('ATECLOUD 社区指南')[0]
    .trim();
  const matched = text.match(/^(.+?[！!])\s+(.+)$/);
  if (matched) {
    return `${matched[1]}\n\n${matched[2]}`;
  }
  return text;
}

const PinList: FC<IProps> = ({ data }) => {
  const { t } = useTranslation('translation', { keyPrefix: 'question' });
  const navigate = useNavigate();
  if (!data?.length) return null;

  return (
    <>
      {data.map((item) => {
        const href = pathFactory.questionLanding(item.id, item.url_title);
        return (
          <ListGroup.Item
            action
            as="li"
            key={item.id}
            onClick={() => navigate(href)}
            className="py-3 px-2 border-start-0 border-end-0 position-relative pointer">
            <h5 className="text-wrap text-break">
              <NavLink
                className="link-dark d-block"
                onClick={(e) => e.stopPropagation()}
                to={href}>
                {item.title}
                {item.status === 2 ? ` [${t('closed')}]` : ''}
              </NavLink>
            </h5>
            {item.description ? (
              <div className="mb-2">
                <NavLink
                  to={href}
                  className="d-block small text-body"
                  style={{ whiteSpace: 'pre-wrap' }}
                  onClick={(e) => e.stopPropagation()}>
                  {pinExcerpt(item.description)}
                </NavLink>
              </div>
            ) : null}
            <div className="question-tags mb-12">
              {Array.isArray(item.tags)
                ? item.tags.map((tag, index) => (
                    <Tag
                      key={tag.slug_name}
                      className={`${
                        item.tags.length - 1 === index ? '' : 'me-1'
                      }`}
                      data={tag}
                    />
                  ))
                : null}
            </div>
            <div className="small text-secondary">
              <Counts
                data={{
                  votes: item.vote_count,
                  answers: item.answer_count,
                  views: item.view_count,
                }}
                isAccepted={item.accepted_answer_id >= 1}
                className="mt-2 mt-md-0"
              />
            </div>
          </ListGroup.Item>
        );
      })}
    </>
  );
};

export default PinList;
